/**
 * # Messaging Mixin
 * Add functionality for showing messages related to a form field.
 *
 * @example
 * mixin(Component, messaging);
 *
 * @module mixin/messaging.js
 */
import animateHeight from '../helpers/animation/height';

export default {

  /**
   * Set the error state.
   * @param {String} message Optional
   */
  setError(message) {

    // Animate down
    if (!this._isMessageVisible()) {
      this._showMessage();
    }

    this.clearWarning();
    this.clearSuccess();
    this.clearInfo();

    this.el.setAttribute('data-error', true);

    if (message) {
      this.setMessage(message);
    }

    return this;
  },


  /**
   * Set the error state.
   */
  clearError() {
    this.el.removeAttribute('data-error', true);
    return this;
  },


  /**
   * Set the warning state.
   * @param {String} message Optional
   */
  setWarning(message) {

    // Animate down
    if (!this._isMessageVisible()) {
      this._showMessage();
    }

    this.clearError();
    this.clearSuccess();
    this.clearInfo();

    this.el.setAttribute('data-warning', true);

    if (message) {
      this.setMessage(message);
    }

    return this;
  },


  /**
   * Set the error state.
   */
  clearWarning() {
    this.el.removeAttribute('data-warning', true);
    return this;
  },


  /**
   * Set the success state.
   * @param {String} message Optional
   */
  setSuccess(message) {

    // Animate down
    if (!this._isMessageVisible()) {
      this._showMessage();
    }

    this.clearError();
    this.clearWarning();
    this.clearInfo();

    this.el.setAttribute('data-success', true);

    if (message) {
      this.setMessage(message);
    }

    return this;
  },


  /**
   * Set the success state.
   */
  clearSuccess() {
    this.el.removeAttribute('data-success', true);
    return this;
  },


  /**
   * Set the info state.
   * @param {String} message Optional
   */
  setInfo(message) {

    // Animate down
    if (!this._isMessageVisible()) {
      this._showMessage();
    }

    this.clearError();
    this.clearWarning();
    this.clearSuccess();

    this.el.setAttribute('data-info', true);

    if (message) {
      this.setMessage(message);
    }

    return this;
  },


  /**
   * Set the info state.
   */
  clearInfo() {
    this.el.removeAttribute('data-info', true);
    return this;
  },


  /**
   * Clear all messages.
   */
  clearMessages() {
    this._hideMessage(function() {
      this.clearError();
      this.clearWarning();
      this.clearSuccess();
      this.clearInfo();
    }.bind(this));
    return this;
  },


  /**
   * Set the message text.
   * @param {String} message
   */
  setMessage(message) {
    this.messageEl.innerHTML = message;
    return this;
  },


  /**
   * Show the message
   */
  _showMessage() {

    if (!this.messageEl.parentNode) {
      this.el.appendChild(this.messageEl);
    }

    animateHeight({
      el: this.el,
      toggleEl: this.messageEl
    });
  },


  /**
   * Hide the message.
   * @param {Function} callback
   */
  _hideMessage(callback) {

    animateHeight({
      el: this.el,
      toggleEl: this.messageEl,
      toggleValue: 'none',
      action: 'collapse',
      onComplete: callback
    });
  },


  /**
   * Is the message currently visible?
   * @return {Boolean}
   */
  _isMessageVisible() {
    return this.el.getAttribute('data-error') || this.el.getAttribute('data-warning') || this.el.getAttribute('data-success');
  }
};
